/**
 * File-import data hooks (FE-4, PHASE1 §4.1/§7.3).
 *
 * Upload stages a draft batch and returns a preview (persists NO transactions);
 * commit writes the selected rows and, because it touches balances + the inbox,
 * invalidates `['transactions']` + `['inbox']` + `['accounts']`. Discard drops
 * the draft. The static profile list is cached indefinitely.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  BankProfile,
  ImportBatchOut,
  ImportCommitIn,
  ImportCommitOut,
  ImportPreviewOut,
  ImportProfileOut,
} from '../types.ts';

// ── Queries ────────────────────────────────────────────────────────────────

/** The static list of the 4 supported bank profiles. */
export function useImportProfiles(): UseQueryResult<ImportProfileOut[]> {
  return useQuery({
    queryKey: qk.importProfiles,
    queryFn: () => api.get<ImportProfileOut[]>('/api/imports/profiles'),
    staleTime: Infinity,
  });
}

/** Recent import batches (history). */
export function useImportHistory(limit = 20): UseQueryResult<ImportBatchOut[]> {
  return useQuery({
    queryKey: [...qk.imports, 'history', limit],
    queryFn: () => api.get<ImportBatchOut[]>(`/api/imports?limit=${limit}`),
    staleTime: 30 * 1000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export interface UploadImportArgs {
  accountId: number;
  /** Omit to let the server sniff the format. */
  profile?: BankProfile;
  file: File;
}

/** Upload a statement file → staged preview (no transactions persisted). */
export function useUploadImport(): UseMutationResult<ImportPreviewOut, Error, UploadImportArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, profile, file }: UploadImportArgs) => {
      const form = new FormData();
      form.append('file', file);
      form.append('account_id', String(accountId));
      if (profile) form.append('profile', profile);
      return api.postForm<ImportPreviewOut>('/api/imports', form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.imports });
    },
  });
}

/** Commit a staged batch: writes new (+ selected possibles) minus excludes. */
export function useCommitImport(): UseMutationResult<
  ImportCommitOut,
  Error,
  { importId: number; body: ImportCommitIn }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ importId, body }: { importId: number; body: ImportCommitIn }) =>
      api.post<ImportCommitOut>(`/api/imports/${importId}/commit`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.transactions });
      void qc.invalidateQueries({ queryKey: qk.inbox });
      void qc.invalidateQueries({ queryKey: qk.accounts });
      void qc.invalidateQueries({ queryKey: qk.imports });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}

/** Discard a staged draft batch. */
export function useDiscardImport(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (importId: number) => api.del(`/api/imports/${importId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.imports });
    },
  });
}
